import React, { useEffect, useMemo, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiChevronDown, FiX, FiLogOut, FiSearch } from "react-icons/fi";
import { useSession } from "./PortalGate";
import { ACCOUNT, roleLabel } from "./accessWords";
import { NAV_CATEGORIES, categoryOf, navFor } from "../navigation";

const ROLE_RANK = { viewer: 1, operator: 2, tenant_admin: 3, super_admin: 4 };

// Which categories the operator left open, remembered across reloads.
//
// Opening every category at once puts around thirty items on screen and makes
// the list something to scan rather than something to use. The default is
// therefore the everyday group and the category you are currently in; anything
// the operator opens or closes is kept.
const OPEN_CATS_KEY = "nabx.sidebar.openCategories";

function readOpenCats() {
  try {
    const raw = localStorage.getItem(OPEN_CATS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null; // private window, blocked storage: fall back to the default
  }
}

function writeOpenCats(open) {
  try {
    localStorage.setItem(OPEN_CATS_KEY, JSON.stringify(open));
  } catch {
    /* a remembered preference is a convenience, never a requirement */
  }
}

export default function Sidebar({
  activeView,
  onNavigate,
  nodes,
  selectedNode,
  onSelectNode,
  collapsed,
  onToggleCollapse,
  health,
  mobileOpen,
  onCloseMobile,
  nodeMode,
  singleEdge,
  phone,
}) {
  const session = useSession();

  // What this edge and this build offer, then what this account may open.
  // Outside the portal the session is not managed, so role-gated groups and
  // screens are portal-only. A group left with nothing it may open is dropped.
  const allowed = (gate) =>
    !gate.minRole || (session.managed && (ROLE_RANK[session.role] || 0) >= ROLE_RANK[gate.minRole]);
  const navCategories = navFor({ mode: nodeMode, singleEdge, phone })
    .filter(allowed)
    .map((c) => ({ ...c, items: c.items.filter(allowed) }))
    .filter((c) => c.items.length > 0);

  // Which categories are open: the defaults, overridden by whatever the
  // operator last chose.
  const [expandedCats, setExpandedCats] = useState(() => {
    const open = Object.fromEntries(NAV_CATEGORIES.filter((c) => c.defaultOpen).map((c) => [c.id, true]));
    const cat = categoryOf(activeView);
    if (cat) open[cat] = true;
    return { ...open, ...readOpenCats() };
  });

  // Navigating from somewhere other than the sidebar, a deep link or the
  // guided tour, must reveal where it landed rather than leave the operator
  // looking at a closed category.
  useEffect(() => {
    const cat = categoryOf(activeView);
    if (!cat) return;
    setExpandedCats((prev) => (prev[cat] ? prev : { ...prev, [cat]: true }));
  }, [activeView]);

  const toggleCat = (catId) => {
    setExpandedCats((prev) => {
      const next = { ...prev, [catId]: !prev[catId] };
      writeOpenCats(next);
      return next;
    });
  };

  // Typing beats scanning once there are this many destinations. A query
  // searches every category at once, so nothing is hidden behind a closed one.
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return null;
    const out = [];
    for (const cat of navCategories) {
      const items = cat.items.filter(
        (it) => it.label.toLowerCase().includes(q) || cat.label.toLowerCase().includes(q),
      );
      if (items.length > 0) out.push({ ...cat, items });
    }
    return out;
  }, [q, navCategories]);
  const shown = filtered || navCategories;

  const handleNav = (key) => {
    onNavigate(key);
    if (onCloseMobile) onCloseMobile();
  };

  return (
    <>
      {mobileOpen && (
        <div className="sidebar-mobile-backdrop" onClick={onCloseMobile} aria-hidden="true" />
      )}

      <aside
        className={`sidebar ${collapsed ? "collapsed" : ""} ${mobileOpen ? "mobile-open" : ""}`}
        role="navigation"
        aria-label="Main navigation"
      >
        {/* ── Logo ── */}
        <div className="sidebar-logo">
          <div className="sidebar-logo-mark">
            <img src={`${process.env.PUBLIC_URL}/sidebar-logo-w.png`} alt="Weavian" width="28" height="28" />
          </div>
          {!collapsed && (
            <div className="sidebar-logo-text">
              <span className="sidebar-brand">Weavian</span>
              <span className="sidebar-edition">Edge Console</span>
            </div>
          )}
          {mobileOpen && (
            <button className="sidebar-mobile-close" onClick={onCloseMobile} aria-label="Close navigation">
              <FiX size={20} />
            </button>
          )}
        </div>

        {/* ── Find a destination by name ── */}
        {!collapsed && (
          <div className="sidebar-filter">
            <FiSearch className="sidebar-filter-icon" size={14} aria-hidden="true" />
            <input
              type="search"
              className="sidebar-filter-input"
              placeholder="Find a screen"
              aria-label="Find a screen"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setQuery("");
              }}
            />
          </div>
        )}

        {/* ── Navigation with collapsible categories ── */}
        <nav className="sidebar-nav" role="tablist" aria-label="Console sections">
          {!collapsed && filtered && filtered.length === 0 && (
            <div className="sidebar-empty">No screen matches “{query.trim()}”.</div>
          )}
          {shown.map((cat) => {
            // A search shows what it found, whatever the operator last left open.
            // Boolean, not undefined: React drops an undefined aria-expanded
            // entirely, so a collapsed category would announce nothing about
            // being collapsible at all.
            //
            // A category of one is always open. Folding a single item under its
            // own header saves no space and costs every visit a click.
            const single = cat.items.length === 1;
            const isExpanded = filtered || single ? true : !!expandedCats[cat.id];
            return (
              <div key={cat.id} className="sidebar-category">
                {!collapsed && single && (
                  <div className="sidebar-category-header is-static">
                    <span className="sidebar-category-label">{cat.label}</span>
                  </div>
                )}
                {!collapsed && !single && (
                  <button
                    className={`sidebar-category-header ${isExpanded ? "expanded" : ""}`}
                    onClick={() => toggleCat(cat.id)}
                    aria-expanded={isExpanded}
                    title={cat.label}
                  >
                    <span className="sidebar-category-label">{cat.label}</span>
                    <FiChevronDown
                      className={`sidebar-category-chevron ${isExpanded ? "rotated" : ""}`}
                      size={14}
                    />
                  </button>
                )}
                <div
                  className={`sidebar-category-items ${
                    collapsed || isExpanded ? "expanded" : "collapsed-items"
                  }`}
                >
                  <div className="sidebar-category-items-inner">
                    {cat.items.map((item) => {
                      const Icon = item.icon;
                      const isActive = activeView === item.key;
                      return (
                        <button
                          key={item.key}
                          // Every panel is addressable by a walkthrough, so a
                          // change with no anchored control still guides here.
                          data-tour={`nav.${item.key}`}
                          className={`sidebar-item ${isActive ? "active" : ""}`}
                          onClick={() => handleNav(item.key)}
                          title={collapsed ? item.label : undefined}
                          role="tab"
                          aria-selected={isActive}
                          aria-label={item.label}
                        >
                          <div className="sidebar-item-indicator" />
                          <Icon className="sidebar-icon" size={20} />
                          {!collapsed && (
                            <span className="sidebar-label">{item.label}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        {/* ── Node selector ── */}
        <div className="sidebar-footer">
          {!collapsed && !singleEdge && nodes.length > 0 && (
            <div className="sidebar-node-section">
              <div className="sidebar-node-label">EDGE NODE</div>
              <select
                className="sidebar-node-dropdown"
                value={selectedNode}
                onChange={(e) => onSelectNode(e.target.value)}
                aria-label="Select edge node"
              >
                <option value="">Select node…</option>
                {nodes.map((node) => (
                  <option key={node.node_id} value={node.node_id}>
                    {node.node_id}
                  </option>
                ))}
              </select>
              {selectedNode && (
                <div className="sidebar-node-status">
                  <span
                    className={`sidebar-status-dot ${
                      health.status === "ok" ? "online" : "offline"
                    }`}
                  />
                  <span className="sidebar-status-text">
                    {health.status === "ok" ? "Connected" : "Unreachable"}
                  </span>
                </div>
              )}
            </div>
          )}

          {collapsed && selectedNode && (
            <div
              className="sidebar-node-collapsed"
              title={`${selectedNode} — ${
                health.status === "ok" ? "Connected" : "Unreachable"
              }`}
            >
              <span
                className={`sidebar-status-dot ${
                  health.status === "ok" ? "online" : "offline"
                }`}
              />
            </div>
          )}

          {/* ── Signed-in account, which opens the person's own account ── */}
          {session.managed && session.user && (
            <div
              className="sidebar-account"
              title={`${session.user.username} — ${roleLabel(session.role)}`}
            >
              {!collapsed && (
                <button
                  type="button"
                  className={`sidebar-account-info ${activeView === "account" ? "active" : ""}`}
                  onClick={() => handleNav("account")}
                  aria-current={activeView === "account" ? "page" : undefined}
                  title={ACCOUNT}
                >
                  <span className="sidebar-account-name">
                    {session.user.username}
                  </span>
                  <span className="sidebar-account-role">
                    {roleLabel(session.role)}
                    {!session.isSuperAdmin &&
                      session.domains.length > 0 &&
                      ` · ${session.domains.join(", ")}`}
                  </span>
                </button>
              )}
              <button
                type="button"
                className="sidebar-account-btn"
                onClick={session.logout}
                aria-label="Sign out"
                title="Sign out"
              >
                <FiLogOut size={15} />
              </button>
            </div>
          )}

          <button
            className="sidebar-collapse-btn desktop-only"
            onClick={onToggleCollapse}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <FiChevronRight size={16} />
            ) : (
              <FiChevronLeft size={16} />
            )}
          </button>
        </div>
      </aside>
    </>
  );
}
